import {
  VaultDocumentListResponseSchema,
  VaultDocumentSchema,
  VaultFolderListResponseSchema,
  SuggestTemplateFieldsResponseSchema,
} from "@l30/shared";
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

import type {
  VaultFolder,
  VaultDocument,
  VaultDocumentCreate,
  TemplateField,
  SuggestTemplateFieldsRequest,
  SuggestTemplateFieldsResponse,
} from "@l30/shared";

const FOLDERS_KEY = ["vault-folders"] as const;
const DOCUMENTS_KEY = ["vault-documents"] as const;

/**
 * `undefined` — omit X-Tenant (host / staff default).
 * `null` — organisation not chosen yet; queries stay disabled.
 * string — force that organisation's schema.
 */
function tenantOpts(tenantSlug: string | null | undefined) {
  return typeof tenantSlug === "string" && tenantSlug.length > 0
    ? { tenantOverride: tenantSlug }
    : {};
}

function tenantKey(tenantSlug: string | null | undefined): string {
  if (tenantSlug === null) return "unset";
  if (typeof tenantSlug === "string") return tenantSlug;
  return "default";
}

// ─── Folders ───────────────────────────────────────────────

export function useFolders(tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...FOLDERS_KEY, tenantKey(tenantSlug)],
    enabled: tenantSlug !== null,
    queryFn: () =>
      apiFetch("/documents/folders", tenantOpts(tenantSlug), VaultFolderListResponseSchema),
  });
}

export function useCreateFolder(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      parentId: string | null;
      title: string;
      visibleToRoles: string[];
      sharedWithPortal?: boolean;
    }) =>
      apiFetchUnchecked<VaultFolder>("/documents/folders", {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: FOLDERS_KEY }),
  });
}

/** Soft-archive a folder. Admin only — server returns 403 otherwise. */
export function useArchiveFolder(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<VaultFolder>(`/documents/folders/${id}/archive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FOLDERS_KEY });
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEY });
    },
  });
}

/** Restore an archived folder. Admin only. */
export function useUnarchiveFolder(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<VaultFolder>(`/documents/folders/${id}/unarchive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FOLDERS_KEY });
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEY });
    },
  });
}

/** Hard-delete an empty folder. Admin only. */
export function useDeleteFolder(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/documents/folders/${id}`, {
        method: "DELETE",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FOLDERS_KEY });
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEY });
    },
  });
}

// ─── Documents ─────────────────────────────────────────────

export function useDocuments(
  query?: {
    folderId?: string;
    search?: string;
    /** Admin-only: include archived rows alongside active. Server enforces. */
    includeArchived?: boolean;
    /** Admin-only: show only archived rows. Server enforces. */
    archivedOnly?: boolean;
    limit?: number;
    offset?: number;
  },
  tenantSlug?: string | null,
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (query?.folderId) params.set("folderId", query.folderId);
  if (query?.search) params.set("search", query.search);
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.offset) params.set("offset", String(query.offset));

  const qs = params.toString();
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, tenantKey(tenantSlug), query],
    enabled: tenantSlug !== null && (options?.enabled ?? true),
    queryFn: () =>
      apiFetch(
        `/documents${qs ? `?${qs}` : ""}`,
        tenantOpts(tenantSlug),
        VaultDocumentListResponseSchema,
      ),
    // Keep the current page visible while the next loads (assessment U8).
    placeholderData: keepPreviousData,
  });
}

export function useDocument(id: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, tenantKey(tenantSlug), id],
    queryFn: () => apiFetch(`/documents/${id}`, tenantOpts(tenantSlug), VaultDocumentSchema),
    enabled: !!id && tenantSlug !== null,
  });
}

// Re-export so existing call sites (`import { TemplateField } from "../api/documents"`)
// keep working without a churn-only sweep. Source of truth is @l30/shared.
export type { TemplateField };

export function useUpdateTemplateFields(id: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: TemplateField[]) =>
      apiFetchUnchecked<VaultDocument>(`/documents/${id}/template-fields`, {
        method: "PUT",
        body: JSON.stringify({ fields }),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEY });
      qc.invalidateQueries({ queryKey: [...DOCUMENTS_KEY, tenantKey(tenantSlug), id] });
    },
  });
}

/** Smart suggest (page 1 + closing vision). Does not persist until Save fields. */
export function useSuggestTemplateFields(id: string, tenantSlug?: string | null) {
  return useMutation({
    mutationFn: (body: SuggestTemplateFieldsRequest) =>
      apiFetch(
        `/documents/${id}/suggest-template-fields`,
        {
          method: "POST",
          body: JSON.stringify(body),
          ...tenantOpts(tenantSlug),
        },
        SuggestTemplateFieldsResponseSchema,
      ) as Promise<SuggestTemplateFieldsResponse>,
  });
}

export function useDocumentDownloadUrl(id: string, enabled: boolean, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, tenantKey(tenantSlug), id, "download-url"],
    queryFn: () =>
      apiFetchUnchecked<{ downloadUrl: string; expiresAt: string }>(`/documents/${id}/download`, {
        ...tenantOpts(tenantSlug),
      }),
    enabled: enabled && !!id && tenantSlug !== null,
    staleTime: 4 * 60 * 1000, // signed URLs typically valid ~5 min
  });
}

export function useCreateDocument(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: VaultDocumentCreate) =>
      apiFetchUnchecked<VaultDocument>("/documents", {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

export function useUpdateDocument(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      title?: string;
      tags?: string[];
      archived?: boolean;
    }) =>
      apiFetchUnchecked<VaultDocument>(`/documents/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

/** Hard-delete a document. Admin only — server returns 403 otherwise. */
export function useDeleteDocument(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/documents/${id}`, {
        method: "DELETE",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

/** Soft-delete (archive). Admin only. */
export function useArchiveDocument(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<VaultDocument>(`/documents/${id}/archive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

/** Restore an archived document. Admin only. */
export function useUnarchiveDocument(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<VaultDocument>(`/documents/${id}/unarchive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

export function useDocumentDownload(tenantSlug?: string | null) {
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetchUnchecked<{ downloadUrl: string; filename: string }>(
        `/documents/${id}/download`,
        tenantOpts(tenantSlug),
      );
      // Trigger browser download
      const a = document.createElement("a");
      a.href = result.downloadUrl;
      a.download = result.filename;
      a.click();
      return result;
    },
  });
}

export function useUploadToken(tenantSlug?: string | null) {
  return useMutation({
    mutationFn: (data: { filename: string; contentType: string; folderId: string }) =>
      apiFetchUnchecked<{ uploadUrl: string; blobKey: string; expiresAt: string }>(
        "/documents/upload-token",
        {
          method: "POST",
          body: JSON.stringify(data),
          ...tenantOpts(tenantSlug),
        },
      ),
  });
}

export function useDocumentAudit(documentId: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, tenantKey(tenantSlug), documentId, "audit"],
    queryFn: () =>
      apiFetchUnchecked<{
        audit: Array<{ id: string; action: string; userId: string; createdAt: string }>;
      }>(`/documents/${documentId}/audit`, tenantOpts(tenantSlug)),
    enabled: !!documentId && tenantSlug !== null,
  });
}
